import React, { useState } from "react";

import {
	Badge,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Link,
	Typography,
	useTheme
} from "@material-ui/core";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import PageTitle from "components/PageTitle";
import makeStyles from "hooks/makeStyles";
import useBusiness from "hooks/useBusiness";
import useCart from "hooks/useCart";
import AppLogoIcon from "icons/AppLogoIcon";
import CartIcon from "icons/CartIcon";
import InformationIcon from "icons/InformationIcon";
import MapPinIcon from "icons/MapPinIcon";
import PhoneIcon from "icons/PhoneIcon";

const useStyles = makeStyles(theme => ({
	header: {
		marginTop: theme.spacing(1),
		display: "flex",
		flex: 1,
		flexDirection: "row",
		alignItems: "left",
		justifyContent: "space-between"
	},
	title: {
		display: "flex",
		flex: 1,
		flexDirection: "column",
		alignItems: "left",
		justifyContent: "space-between"
	},
	environment: {
		backgroundColor: "red",
		color: "#fff",
		borderRadius: 8,
		position: "absolute",
		top: 10,
		right: 150,
		padding: 5
	},
	description: {
		display: "flex",
		flex: 1,
		flexDirection: "row",
		alignItems: "left"
	},
	descriptionColumn: {
		display: "flex",
		flex: 1,
		flexDirection: "column",
		alignItems: "left"
	},
	titleText: {
		textTransform: "uppercase"
	},
	section: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		"&:hover": {
			textDecoration: "none"
		}
	},
	informationButton: {
		padding: 0,
		width: "16px",
		marginLeft: theme.spacing(2),
		verticalAlign: "text-top",
		marginTop: theme.spacing(2)
	},
	icon: {
		height: theme.spacing(2),
		marginRight: theme.spacing(0.5)
	},
	text: {
		fontSize: 12,
		whiteSpace: "nowrap"
	},
	textDivider: {
		fontSize: 12,
		whiteSpace: "nowrap",
		marginRight: theme.spacing(1),
		marginLeft: theme.spacing(1),
		height: 13,
		marginTop: "3px"
	},
	categoryText: {
		textTransform: "uppercase"
	},
	logo: {
		height: 84
	},
	user: {},
	logoIcon: {
		marginRight: theme.spacing(1)
	},
	fab: {
		position: "relative",
		height: "84px"
	},
	cart: {
		position: "relative",
		marginRight: theme.spacing(1),
		width: "64px",
		height: "64px",
		background: theme.palette.primary.main,
		borderRadius: "50%"
	},
	cartIcon: {
		marginRight: theme.spacing(1),
		width: "64px",
		height: "64px",
		background: theme.palette.primary.main,
		borderRadius: "50%"
	},
	userIcon: {},
	badge: {
		position: "absolute",
		top: "13px",
		right: "13px"
	},
	divider: {
		background: "#828282",
		width: "2px",
		marginRight: theme.spacing(4),
		marginLeft: theme.spacing(4)
	},
	logoDivider: {
		background: "#828282",
		width: "2px",
		marginRight: theme.spacing(2),
		marginLeft: theme.spacing(2)
	},
	textLink: {
		fontSize: 12,
		whiteSpace: "nowrap",
		textDecoration: "none",
		color: theme.palette.text.primary
	},
	login: {},
	loginText: {
		marginRight: theme.spacing(4),
		marginTop: theme.spacing(4),
		fontStyle: "normal",
		fontWeight: "normal",
		fontSize: "18px",
		lineHeight: "17px",
		display: "flex",
		alignItems: "center",
		color: theme.palette.text.secondary,
		textTransform: "uppercase"
	}
}));

interface Props {
	name: string;
	logoUrl?: string | null;
	mapUrl?: string | null;
	isReceiptMode?: boolean;
	categories: {
		id: string;
		name: string;
		localeNames: Record<string, string>;
	}[];
	address: string;
	phoneNumber: string | null;
	displayPhoneNumber: string | null;
	environment: string | null | undefined;
}

const BusinessHeaderDesktop = ({
	name,
	logoUrl,
	address,
	mapUrl,
	phoneNumber,
	displayPhoneNumber,
	environment,
	isReceiptMode = false
}: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const navigate = useNavigate();
	const theme = useTheme();
	const { cartItemsCount } = useCart();
	const { business } = useBusiness();
	const [error, setError] = useState();

	const onCompanyInfoClicked = () => {
		navigate("#info");
	};

	const onCartClicked = () => {
		if (business.pause) {
			setError(t("Pausing", { name: business.name }));
		} else {
			navigate("#checkout");
		}
	};

	const mapLinkUrl = mapUrl ? mapUrl : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

	return (
		<>
			<PageTitle businessName={name} subTitle={t("pageTitle")} />
			<Box className={classes.header}>
				{!isReceiptMode && (
					<Box className={classes.logo}>
						<Box className={classes.fab}>
							{logoUrl ? <img src={logoUrl} className={classes.logo} /> : <AppLogoIcon className={classes.logoIcon} />}
						</Box>
					</Box>
				)}
				{!isReceiptMode && <Box className={classes.logoDivider}></Box>}
				<Box className={classes.title}>
					<Typography noWrap className={classes.titleText} variant="h1">
						{name}
						{!isReceiptMode && (
							<IconButton component="a" className={classes.informationButton} onClick={onCompanyInfoClicked}>
								<InformationIcon />
							</IconButton>
						)}
					</Typography>
					{environment && <Box className={classes.environment}>{environment}</Box>}
					<Box className={isReceiptMode ? classes.descriptionColumn : classes.description}>
						<Link className={classes.section} href={mapLinkUrl} target="_blank">
							<MapPinIcon className={classes.icon} />
							<Typography className={classes.textLink}>{address}</Typography>
						</Link>
						{displayPhoneNumber && (
							<Link href={`tel:${phoneNumber}`} className={classes.section}>
								<PhoneIcon className={isReceiptMode ? classes.icon : classes.textDivider} />
								<Typography className={classes.textLink}>{displayPhoneNumber}</Typography>
							</Link>
						)}
						{/* <ForkKnifeIcon className={classes.icon} />
						<Typography className={clsx(classes.text, classes.categoryText)}>
							{categories.map((category, index) => (
								<Fragment key={category.id}>
									{index === 0 ? null : <> &bull; </>}
									{localize(category.localeNames, category.name)}
								</Fragment>
							))}
						</Typography> */}
					</Box>
				</Box>
				{/* <Box className={classes.login}>
					<Typography noWrap className={classes.loginText} variant="h6">
						{t("login")}
					</Typography>
				</Box>
				<Box className={classes.user}>
					<Box className={classes.fab} onClick={onCartClicked}>
						<UserInfoIcon className={classes.userIcon} />
					</Box>
				</Box> */}
				{!isReceiptMode && (
					<>
						<Box className={classes.divider}></Box>
						<Box className={classes.cart}>
							<IconButton className={classes.cartIcon} onClick={onCartClicked}>
								<CartIcon fill={theme.palette.primary.contrastText} height={22} width={22} />
								{cartItemsCount > 0 && (
									<Badge badgeContent={cartItemsCount} color={"secondary"} className={classes.badge}></Badge>
								)}
							</IconButton>
						</Box>
					</>
				)}
			</Box>

			<Dialog
				open={!!error}
				onClose={() => setError(undefined)}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">{t("Oppsss")}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">{error}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setError(undefined)}>{t("Ok")}</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default BusinessHeaderDesktop;
